import { ArrowDown, Loader2 } from 'lucide-react';
import { usePullToRefresh } from '@/pages/(app-webview)/hooks/usePullToRefresh';

const THRESHOLD = 64;

interface PullToRefreshProps {
  onRefresh: () => Promise<void>;
  disabled?: boolean;
  children: React.ReactNode;
}

export function PullToRefresh({
  onRefresh,
  disabled = false,
  children,
}: PullToRefreshProps) {
  const { containerRef, pullDistance, state } = usePullToRefresh({
    onRefresh,
    threshold: THRESHOLD,
    disabled,
  });

  const isActive = state !== 'idle';
  const progress = Math.min(pullDistance / THRESHOLD, 1);
  const rotation = progress * 180;

  // disabled일 때는 스크롤 래퍼 없이 children만 렌더링
  if (disabled) {
    return <>{children}</>;
  }

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full overflow-y-auto"
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      {/* Indicator */}
      <div
        className="pointer-events-none absolute left-0 right-0 z-50 flex items-center justify-center"
        style={{
          top: 0,
          height: 40,
          transform: `translateY(${isActive ? pullDistance - 40 : -40}px)`,
          transition:
            state === 'idle'
              ? 'transform 300ms cubic-bezier(0.2, 0, 0, 1)'
              : 'none',
          opacity: isActive ? 1 : 0,
        }}
      >
        <div
          className="flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-md dark:bg-neutral-800"
          style={{
            transform:
              state === 'triggered'
                ? 'scale(1.15)'
                : state === 'refreshing'
                  ? 'scale(1)'
                  : `scale(${0.8 + progress * 0.2})`,
            transition: 'transform 150ms ease-out',
          }}
        >
          {state === 'refreshing' ? (
            <Loader2 className="h-4 w-4 animate-spin text-orange-500" />
          ) : (
            <ArrowDown
              className="h-4 w-4 text-orange-500"
              style={{
                transform: `rotate(${rotation}deg)`,
                transition: state === 'idle' ? 'transform 200ms' : 'none',
              }}
            />
          )}
        </div>
      </div>

      {/* Content */}
      <div
        style={{
          transform: `translateY(${isActive ? pullDistance : 0}px)`,
          transition:
            state === 'idle'
              ? 'transform 300ms cubic-bezier(0.2, 0, 0, 1)'
              : 'none',
        }}
      >
        {children}
      </div>
    </div>
  );
}
